import i18n from "@/plugins/i18n";
import Meta from "./ModuleMeta";
import widgetMixin from "./widgetMixin";
import { WidgetExportTypes } from "./CsharpEnum";
import { aggregationTypes } from "@/_helpers/aggregationHelper";

export const chartMixin = {
  mixins: [widgetMixin],
  computed: {
    metaKeys() {
      return Meta.Enum;
    },

    chartType: (ctx) => (slotId) => {
      var chartTypeMeta = ctx.getMeta(ctx.metaKeys.CHART_TYPE, slotId);
      if (chartTypeMeta === undefined) return "area";

      return chartTypeMeta.value;
    },

    chartTypes() {
      return [
        {
          name: this.$t("module.chart.types.area"),
          value: "area",
          icon: "mdi-chart-areaspline-variant",
        },
        {
          name: this.$t("module.chart.types.line"),
          value: "line",
          icon: "mdi-chart-areaspline",
        },
        {
          name: this.$t("module.chart.types.bar"),
          value: "bar",
          icon: "mdi-chart-bar",
        },
        {
          name: this.$t("module.chart.types.scatter"),
          value: "scatter",
          icon: "mdi-chart-scatter-plot",
        },
        {
          name: this.$t("module.chart.types.trend"),
          value: "trend",
          icon: "mdi-chart-bar-stacked",
        },
      ];
    },

    aggregationTypes() {
      return aggregationTypes.map((x) => {
        return { value: x, name: this.$t(`aggregation.types.${x}`) };
      });
    },

    shouldSyncDataZoom() {
      return this.getMetaValueAsBool(this.metaKeys.CHART_SYNC_TIME);
    },

    hideChartExport() {
      return this.getMetaValueAsBool(this.metaKeys.CHART_HIDE_EXPORT);
    },

    meta() {
      return this?.widget?.meta;
    },

    isVertical() {
      return this.getMetaValueAsBool(this.metaKeys.VERTICAL);
    },

    base() {
      return {
        legend: {
          show: true,
          data: [],
        },

        grid: {
          top: 55,
          bottom: 50,
          left: 80,
          right: 80,
        },

        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
          },
        },

        xAxis: {
          type: "time",
          min: undefined,
          max: undefined,
          splitLine: {
            show: false,
          },
          axisLabel: {
            formatter: {
              day: "{d} {MMM}",
            },
          },
          axisLine: { lineStyle: {} },
        },

        yAxis: [],

        dataZoom: [
          { show: true, type: "inside", throttle: 50, start: 0, end: 100 },
        ],

        series: [],
      };
    },

    baseSeriesLine: () => (slotId, name) => {
      return {
        type: "line",
        slotId: slotId,
        name: name,
        data: [],
        areaStyle: undefined,
        axisLabel: {
          color: "#999",
        },
        itemStyle: {},
        lineStyle: { width: 1 },
        step: false,
        markLine: { data: [] },
      };
    },

    baseSeriesScatter: () => (slotId, name) => {
      return {
        type: "scatter",
        slotId: slotId,
        name: name,
        data: [],
        areaStyle: undefined,
        itemStyle: {},
        lineStyle: {},
        axisLabel: {
          color: "#999",
        },
        step: false,
        markLine: { data: [] },
      };
    },

    baseSeriesArea: () => (slotId, name) => {
      return {
        type: "line",
        slotId: slotId,
        name: name,
        data: [],
        areaStyle: { opacity: 0.15 },
        emphasis: {
          focus: "series",
          areaStyle: { opacity: 0.15 },
        },
        itemStyle: {},
        lineStyle: { width: 1 },
        axisLabel: {
          color: "#999",
        },
        step: false,
        markLine: { data: [] },
      };
    },

    baseSeriesBar: () => (slotId, name) => {
      return {
        type: "bar",
        slotId: slotId,
        name: name,
        data: [],
        areaStyle: { opacity: 0.35 },
        itemStyle: {},
        lineStyle: {},
        axisLabel: {
          color: "#999",
        },
        step: false,
        markLine: { data: [] },
      };
    },

    yAxisBaseDefault: () => (slotId, name, min, max) => {
      return {
        default: true,
        type: "value",
        slotIds: [slotId],
        name: name,
        min: min,
        max: max,
        axisLabel: {
          color: "#999",
        },
        axisLine: {
          show: true,
          lineStyle: {
            width: 2,
          },
        },
      };
    },

    yAxisBaseSync: () => (slotId, name, min, max, len, inverse) => {
      return {
        type: "value",
        slotId: slotId,
        slotIds: [slotId],
        name: name,
        position: len > 0 ? "right" : "left",
        offset: len > 1 ? (len - 1) * 70 : 4,
        nameLocation: (len - 1) % 2 == 1 ? "start" : "end",
        min: min,
        max: max,
        inverse: inverse,
        splitLine: { show: false },
        axisLine: {
          show: true,
          lineStyle: {
            width: 2,
          },
        },
        axisLabel: {
          show: true,
        },
      };
    },

    metaCount: (ctx) => (key) => {
      if (ctx.meta === undefined) return 0;

      return ctx.meta.filter((d) => d.key === key).length;
    },

    defaultFromDate() {
      return new Date().addHours(-24);
    },

    widgetExportTypes: () => WidgetExportTypes,

    settingsOptionsList() {
      return [
        {
          text: this.$t("export.widget.png"),
          value: WidgetExportTypes.PNG,
          icon: "mdi-chart-areaspline",
        },
        {
          text: this.$t("export.widget.csv"),
          value: WidgetExportTypes.CSV,
          icon: "mdi-file-delimited",
        },
        {
          text: this.$t("export.widget.json"),
          value: WidgetExportTypes.JSON,
          icon: "mdi-code-json",
        },
      ];
    },
  },

  methods: {
    setAxisDateRange(baseAxis, from, to) {
      if (typeof from !== "object") {
        from = new Date(from);
      }

      if (typeof to !== "object") {
        to = new Date(to);
      }

      return { ...baseAxis, min: from, max: to };
    },

    getFromDateOrDefault() {
      switch (this.getMeta(this.metaKeys.DEFAULT_TIMESPAN)?.value) {
        case "hour":
          return new Date().addHours(-1);
        case "5_hour":
          return new Date().addHours(-5);
        case "24_hour":
          return new Date().addHours(-24);
        case "7_days":
          return new Date().addDays(-7);
        case "30_days":
          return new Date().addDays(-30);
        default:
          return this.defaultFromDate;
      }
    },

    verticalAxis(options) {
      let temp = options.xAxis;
      options.xAxis = options.yAxis;
      options.yAxis = temp;

      let i = 0;
      for (var ax of options.xAxis) {
        ax.nameLocation = "end";
        ax.nameGap = 10;
        ax.axisLine = { show: true, onZero: false, margin: 4 };
        ax.offset = 25 * i;
        i++;
      }

      // Remove all axises that are not default
      for (var ser of options.series) {
        ser.xAxisIndex = ser.yAxisIndex;
        ser.yAxisIndex = undefined;
        ser.nameLocation = "end";
      }

      return options;
    },

    hasData(series) {
      return (
        series !== undefined &&
        series?.length > 0 &&
        series.some((x) => x.data.length > 0)
      );
    },

    addLegend(opts, name) {
      if (opts === undefined || opts.legend === undefined) return;

      if (!opts.legend.data.includes(name)) opts.legend.data.push(name);
    },

    clearLegend(opts) {
      if (opts === undefined || opts.legend === undefined) return;
      opts.legend.data = [];
    },

    setAreaColor(series, color) {
      let areaStyle = { opacity: 0.35 };
      if (series?.areaStyle === undefined)
        return (series.areaStyle = {
          ...areaStyle,
          color: color,
        });

      series.areaStyle.color = color;
      return series;
    },

    generateFakeData(base) {
      var data = [];
      var date = new Date().zeroTimeFromMinutes().addHours(-25);
      const fluc = [1, 0.7, 1, 1.2, 1, 1.2, 0.7, 0.9];
      for (var i = 0; i < 24; ++i) {
        var value =
          base *
          fluc[Math.floor(Math.random() * (10 - 0 + 1) + 0) % fluc.length];
        date = date.addHours(1);

        data.push({
          name: date.toString(),
          value: this.isVertical
            ? [value, new Date(date)]
            : [new Date(date), value],
        });
      }

      return data;
    },

    setTargetSeries(base, value) {
      var targetSeries = {
        type: "line",
        data: [],
        itemStyle: { color: "#33691e" },
        z: 9998,
        markLine: {
          data: this.isVertical ? [{ xAxis: value }] : [{ yAxis: value }],
          silent: true,
          lineStyle: { width: 3 },
          symbolSize: 10,
          symbol: ["arrow", "arrow"],
          label: {
            fontWeight: "bold",
            position: "middle",
            formatter: "{c} | " + i18n.t("module.meta.fields.target"),
          },
        },
      };

      base.series.push(targetSeries);

      return base;
    },

    // ChartLegend is using unaggregated data, Chart is using aggregated data
    calcZoomedData(data, zoom, dateRange) {
      if (data.length == 0) return data;

      let start = this.isVertical
        ? new Date(data[0].value.at(1))
        : new Date(data[0].value.at(0));

      let end = this.isVertical
        ? new Date(data[data.length - 1].value.at(1))
        : new Date(data[data.length - 1].value.at(0));

      if (dateRange?.start !== undefined && dateRange?.end !== undefined) {
        const range = this.getTimeRangeAsDates(dateRange);
        start = range.start;
        end = range.end;
      }

      const serieElapsedTime = end - start;
      const startTime = start.getTime() + (zoom.start / 100) * serieElapsedTime;
      const endTime = start.getTime() + (zoom.end / 100) * serieElapsedTime;

      return data.filter(
        (x) =>
          new Date(this.isVertical ? x.value.at(1) : x.value.at(0)).getTime() >=
            startTime &&
          new Date(this.isVertical ? x.value.at(1) : x.value.at(0)).getTime() <=
            endTime
      );
    },
  },
};
